<!-- eslint-disable @typescript-eslint/no-unsafe-argument -->
<template>
<header
    class="jbg-web navigation-bar" :class="navBarClasses"
>
    <nav
        class="nav-wrapper" :class="{
            mobile: useMobileNav,
            'no-gradient': $route.path.startsWith('/user') || $route.path.startsWith('/signin')
        }"
    >
        <!-- Mobile friendly nav -->
        <template v-if="useMobileNav">
            <div class="mobile-icon-container main-nav">
                <NuxtLink class="logo-link  mobile" :to="localeRoute('/')">
                    <Logo class="logo" :alt="$t('NAVBAR.LOGO')" />
                </NuxtLink>
                <NavBarTray
                    ref="mainNav" nav-menu-type="mainNav" icon-only
                    :options="mobileNavOptions"
                    :text="$t('NAVBAR.MAIN_MENU')"
                    :icon="menuIcons.menu"
                    :show-signin-button="showSignInBtn"
                    :user-authenticated="isLoggedIn" :current-user="currentUser" v-on:menu-select="dropdownMenuSelect"
                    v-on:logout="confirmLogout"
                    v-on:login="navigateToSignin"
                    v-on:open="menuOpen"
                />
            </div>

            <div class="mobile-icon-container icons-right">
                <NavBarDropdown
                    ref="language" nav-menu-type="language" icon-only
                    duo-tone-icon
                    align-right :options="languageOptions"
                    :icon="menuIcons.globe" :text="$t('NAVBAR.LANGUAGE_MENU')"
                    v-on:menu-select="langMenuSelect"
                    v-on:open="menuOpen"
                />
                <Cart
                    class="icon-nav-link shopping-cart" menu-type="cart" icon-only
                    duo-tone-icon align-right
                    shopping-cart :icon="menuIcons.shoppingCart" :cart-counter-number="cartCounterNumber"
                />
                <div v-if="showSignInBtn">
                    <div
                        v-if="!isLoggedIn" tabIndex="0" role="button"
                        :aria="$t('NAVBAR.SIGN_IN')"
                        class="sign-in-icon" v-on:click="navigateToSignin" v-on:keydown.enter="navigateToSignin"
                    >
                        <span class="fa-duotone fa-user icon-nav-link user" />
                    </div>
                    <div v-else class="icon-nav-link">
                        <NavBarDropdown
                            nav-menu-type="user" align-right :options="userMenuOptions"
                            :show-signin-button="showSignInBtn" :user-authenticated="isLoggedIn"
                            v-on:menu-select="userMenuSelect"
                        />
                    </div>
                </div>
            </div>
        </template>

        <!-- Desktop friendly nav -->
        <template v-if="!useMobileNav">
            <div class="main-nav-container">
                <NuxtLink class="logo-link" :to="localeRoute('/')">
                    <Logo class="logo" :alt="$t('NAVBAR.LOGO')" />
                </NuxtLink>
                <NavBarDropdown
                    ref="games"
                    class="nav-link dropdown paragraph games"
                    :class="{ active: $route.path.startsWith('/games') }"
                    align-left
                    nav-menu-type="generic"
                    :options="gameOptions"
                    :text="$t('NAVBAR.GAMES')"
                    v-on:menu-select="dropdownMenuSelect"
                    v-on:open="menuOpen"
                />
                <NuxtLink class="nav-link paragraph how-to-play" :to="localeRoute('/how-to-play')">
                    {{ $t('NAVBAR.HOW_TO_PLAY') }}
                </NuxtLink>
                <div class="item-with-indicator">
                    <NavBarDropdown
                        ref="company" class="nav-link dropdown paragraph company" :class="{
                            active: route.path === '/company' ||
                                route.path === '/blog' ||
                                route.path === '/presskit'
                        }"
                        align-left :options="companyOptions" :text="$t('NAVBAR.COMPANY')"
                        nav-menu-type="generic"
                        v-on:menu-select="dropdownMenuSelect" v-on:open="menuOpen"
                    />
                </div>
                <NuxtLink class="nav-link paragraph support" :to="localeRoute('/support')">
                    {{ $t('NAVBAR.SUPPORT') }}
                </NuxtLink>
                <div class="item-with-indicator">
                    <NavBarDropdown
                        ref="shop" class="nav-link dropdown paragraph shop"
                        :class="{ active: route.path.startsWith('/shop') }" align-left :options="shopOptions"
                        :text="$t('NAVBAR.SHOP')" nav-menu-type="generic" v-on:menu-select="shopMenuSelect"
                        v-on:open="menuOpen"
                    />
                    <div
                        class="triangle-indicator"
                        :class="{ active: route.path.startsWith('/shop') }"
                    />
                </div>
            </div>

            <div class="right-nav-container">
                <button
                    v-if="props.saleButtonVariant"
                    class="icon-nav-link on-sale-btn"
                    type="button"
                    v-on:click="onSaleButtonClick"
                >
                    <div class="on-sale-btn__ring" />
                    <div class="on-sale-btn__inner">
                        <UiIcon icon="tag" alt="sale tag icon" icon-color="white" :classes="['tag-icon']" />
                        <span class="on-sale-btn__label">{{ $t('NAVBAR.ON_SALE') }}</span>
                    </div>
                </button>
                <NavBarDropdown
                    ref="language"
                    class="icon-nav-link language" nav-menu-type="language" icon-only
                    :text="$t('NAVBAR.LANGUAGE_MENU')"
                    duo-tone-icon
                    align-right :options="languageOptions" :icon="menuIcons.globe"
                    :user-authenticated="isLoggedIn"
                    v-on:menu-select="langMenuSelect"
                    v-on:open="menuOpen"
                />

                <Cart
                    class="icon-nav-link shopping-cart" menu-type="cart" icon-only
                    duo-tone-icon align-right
                    shopping-cart :icon="menuIcons.shoppingCart" :cart-counter-number="cartCounterNumber"
                />

                <div v-if="showSignInBtn">
                    <div
                        v-if="!isLoggedIn" tabIndex="0" role="button"
                        class="sign-in-icon"
                        v-on:click="navigateToSignin"
                        v-on:keydown.enter="navigateToSignin"
                    >
                        <span class="fa-duotone fa-user icon-nav-link user" />
                    </div>
                    <div v-else class="icon-nav-link">
                        <NavBarDropdown
                            ref="user"
                            nav-menu-type="user" align-right :options="userMenuOptions"
                            :text="$t('USER_MENU.ACCOUNT')"
                            :show-signin-button="showSignInBtn" :user-authenticated="isLoggedIn"
                            v-on:menu-select="userMenuSelect"
                            v-on:open="menuOpen"
                        />
                    </div>
                </div>
            </div>
        </template>
    </nav>
</header>
</template>

<script setup lang="ts">
import { type Component } from 'vue'
import { isNavigationFailure } from 'vue-router'
import { I18n } from '$services/i18n'
import { useCurrentUser } from '$services/user'
import NavBarDropdown from '$components/navigation/NavBarDropdown.vue'
import NavBarTray from '$components/navigation/NavBarTray.vue'
import Cart from '$components/shopping/Cart.vue'
import InfoModal from '$components/modals/InfoModal.vue'
import Logo from '../../public/images/logo.svg.vue'
import UiIcon from '~/public/images/icons/ui/UiIcon.vue'
import { LaunchDarkly } from '$services/launchdarkly'
import Metric = LaunchDarkly.Metric

export interface MenuOption {
    text: string
    value: string
}

export interface NavMenuOption extends MenuOption {
    icon?: string
    secondaryMenuOptions?: MenuOption[]
    highlight?: boolean
}

export interface MenuItem {
    href: string
    name: string
    svg?: Component
    selected: boolean
}

const iconMap = {
    arrowLeft: 'fa-arrow-left',
    caretDown: 'fa-caret-down',
    globe: 'fa-globe',
    menu: 'fa-regular fa-bars',
    shoppingCart: 'fa-shopping-cart',
    userProfile: 'fa-user',
    xMark: 'fa-xmark'
}

export type menuType = 'cart' | 'language' | 'mainNav' | 'user' | 'generic'

const props = defineProps({
    saleButtonVariant: Boolean
})

const nuxtApp = useNuxtApp()
const currentUser = useCurrentUser()
const menuIcons = ref(iconMap)
const useMobileNav = ref(false)
const cartCounterNumber = ref(0)

const mobileNavOptions = computed<NavMenuOption[]>(() => {
    const options: NavMenuOption[] = [
        {
            value: 'home', text: nuxtApp.$i18n.t('NAVBAR.HOME')
        },
        {
            value: 'games',
            text: nuxtApp.$i18n.t('NAVBAR.GAMES'),
            secondaryMenuOptions: [
                { value: 'games-packs', text: nuxtApp.$i18n.t('NAVBAR.PACKS') },
                { value: 'games', text: nuxtApp.$i18n.t('NAVBAR.GAMES') },
                { value: 'sale', text: nuxtApp.$i18n.t('NAVBAR.ON_SALE') }
            ]
        },
        {
            value: 'how-to-play', text: nuxtApp.$i18n.t('NAVBAR.HOW_TO_PLAY')
        },
        {
            value: 'company',
            text: nuxtApp.$i18n.t('NAVBAR.COMPANY'),
            secondaryMenuOptions: [
                { value: 'blog', text: nuxtApp.$i18n.t('COMPANY.BLOG') },
                { value: 'jobs', text: nuxtApp.$i18n.t('COMPANY.JOBS') },
                { value: 'presskit', text: nuxtApp.$i18n.t('COMPANY.PRESSKIT') }
            ]
        },
        {
            value: 'support', text: nuxtApp.$i18n.t('NAVBAR.SUPPORT')
        },
        {
            value: 'shop',
            text: nuxtApp.$i18n.t('NAVBAR.SHOP'),
            secondaryMenuOptions: [
                { value: 'Games', text: nuxtApp.$i18n.t('NAVBAR.GAMES') },
                { value: 'Merch', text: nuxtApp.$i18n.t('SHOP.MERCH') },
                { value: 'Giftcards', text: nuxtApp.$i18n.t('SHOP.GIFT_CARDS') }
            ]
        }
    ]

    if (props.saleButtonVariant) {
        options.push({ value: 'sale', text: nuxtApp.$i18n.t('NAVBAR.ON_SALE'), highlight: true })
    }

    return options
})

const companyOptions: NavMenuOption[] = [
    {
        value: 'blog', text: nuxtApp.$i18n.t('COMPANY.BLOG')
    },
    {
        value: 'jobs', text: nuxtApp.$i18n.t('COMPANY.JOBS')
    },
    {
        value: 'presskit', text: nuxtApp.$i18n.t('COMPANY.PRESSKIT')
    }
]

const gameOptions: NavMenuOption[] = [
    {
        value: 'games-packs', text: nuxtApp.$i18n.t('NAVBAR.PACKS')
    },
    {
        value: 'games', text: nuxtApp.$i18n.t('NAVBAR.GAMES')
    },
    {
        value: 'sale', text: nuxtApp.$i18n.t('NAVBAR.ON_SALE')
    }
]

const shopOptions: NavMenuOption[] = [
    {
        value: 'Games', text: nuxtApp.$i18n.t('NAVBAR.GAMES')
    },
    {
        value: 'Merch', text: nuxtApp.$i18n.t('SHOP.MERCH')
    },
    {
        value: 'Giftcards', text: nuxtApp.$i18n.t('SHOP.GIFT_CARDS')
    }
]

const languageOptions: NavMenuOption[] = [
    {
        value: 'en', text: nuxtApp.$i18n.t('LANGUAGE_MENU.EN')
    },
    {
        value: 'fr', text: nuxtApp.$i18n.t('LANGUAGE_MENU.FR')
    },
    {
        value: 'it', text: nuxtApp.$i18n.t('LANGUAGE_MENU.IT')
    },
    {
        value: 'de', text: nuxtApp.$i18n.t('LANGUAGE_MENU.DE')
    },
    {
        value: 'es', text: nuxtApp.$i18n.t('LANGUAGE_MENU.ES')
    },
    {
        value: 'es-XL' || 'es-419', text: nuxtApp.$i18n.t('LANGUAGE_MENU.ES-XL')
    },
    {
        value: 'pt-BR', text: nuxtApp.$i18n.t('LANGUAGE_MENU.PT-BR')
    }
]

const userMenuOptions: NavMenuOption[] = [
    {
        value: `user-profile___${nuxtApp.$i18n.locale.value}`, text: nuxtApp.$i18n.t('USER_MENU.PROFILE'), icon: 'fa-user'
    },
    {
        value: 'logout', text: nuxtApp.$i18n.t('USER_MENU.LOGOUT'), icon: 'fa-arrow-right-from-bracket'
    }
]

const isLoggedIn = computed(() => currentUser.isLoggedIn())

const route = useRoute()
const navBarClasses = computed(() => ({
    'no-gradient': route.path.startsWith('/user') || route.path.startsWith('/signin'),
    'bottom-border': route.path.startsWith('/shop')
                    || route.name === 'company'
                    || route.name === 'blog'
                    || route.name === 'presskit',
    'transparent': route.path.startsWith('/streaming')
}))

const showSignInBtn = computed(() => route.name?.toString().startsWith('play')
    || route.path?.startsWith('/user')
    || route.path?.startsWith('/tvcode')
    || route.path?.startsWith('/signin'))

onMounted(() => {
    onScreenResize()

    useMobileNav.value = window.innerWidth < 961
    const lang = languageOptions.find((lang) => lang.value === nuxtApp.$i18n.locale.value)
    if (lang) {
        lang.icon = 'fa-circle-check'
    }
})

// methods
function confirmLogout() {
    void nuxtApp.$modal.showModal(
        InfoModal as Component,
        {
            options: {
                header: nuxtApp.$i18n.t('LOGOUT_MODAL.HEADER'),
                text: nuxtApp.$i18n.t('LOGOUT_MODAL.BODY_TEXT'),
                buttonText: nuxtApp.$i18n.t('LOGOUT_MODAL.BUTTON_TEXT'),
                value: 'logout'
            }
        }
    )
}

function langCheck(value: I18n.Locale) {
    languageOptions.forEach((option) => {
        if (option.value === value) {
            option.icon = 'fa-circle-check'
        } else {
            option.icon = ''
        }
    })
}

function langMenuSelect(value: I18n.Locale) {
    langCheck(value)

    void nuxtApp.$i18n.setLocale(value)
    nuxtApp.$i18n.locale.value = value

    return value
}

const user = ref()
const language = ref()
const mainNav = ref()
function menuOpen() {
    // when a dropdown menu opens, we want to  close all existing menus
    if (user.value) {
        (user.value as typeof NavBarDropdown).close()
    }
    if (language.value) {
        (language.value as typeof NavBarDropdown).close()
    }
    if (mainNav.value) {
        (mainNav.value as typeof NavBarTray).close()
    }
}

const localeRoute = useLocaleRoute()
function dropdownMenuSelect(value: string) {
    if (value === 'sale') {
        nuxtApp.$launchDarkly.track(Metric.SaleButtonNav, {
            uuid: nuxtApp.$launchDarkly.getUuid()
        })
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    void nuxtApp.$router.push(localeRoute({ name: value }))
}

function onSaleButtonClick() {
    nuxtApp.$launchDarkly.track(Metric.SaleButtonNav, {})
    void nuxtApp.$router.push(localeRoute({ name: 'sale' }))
}

function shopMenuSelect(value: string) {
    let route
    switch (value) {
    case 'Games':
        route = { name: 'shop-tab', params: { tab: 'games' } }
        break
    case 'Merch':
        route = { name: 'shop-tab', params: { tab: 'merch' } }
        break
    case 'Giftcards':
        route = { name: 'shop-tab', params: { tab: 'giftcards' } }
        break
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    nuxtApp.$router.push(localeRoute(route)).catch((failure) => {
        if (isNavigationFailure(failure)) {
            // send to shop page on submenu route failure
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            void nuxtApp.$router.push(localeRoute({ name: 'shop-tab', params: { tab: 'games' } }))
        }
    })
}

function navigateToSignin() {
    sessionStorage.setItem('redirect', window.location.href)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    void nuxtApp.$router.push(localeRoute({
        name: 'signin'
    }))
}

function onScreenResize() {
    window.addEventListener('resize', () => {
        useMobileNav.value = window.innerWidth < 961
    })
}

function userMenuSelect(value: string) {
    if (value === 'logout') {
        confirmLogout()
        return
    }

    void nuxtApp.$router.push({ name: value })
}

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

@property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
}

.logo {
    height: 40px;
    padding: 6px 48px 0px 0px;
    width: auto;
    color: var(--neutral-100);

    @include mq-small-and-less {
        height: 32px;
        padding: 0;
    }

    &:hover{
        color: var(--primary-200);
    }
}

.main-nav-container {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.right-nav-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;

    .user {
        width: 17.5px;
        height: 20px;
        color: var(--neutral-100);

        &:hover {
            color: var(--primary-200);
        }
    }

    .on-sale-btn {
        appearance: none;
        border: none;
        outline: none;
        background: none;
        padding: 0;
        margin: 0;
        font: inherit;
        box-sizing: border-box;
        cursor: pointer;
        position: relative;
        width: 140px;
        height: 45px;
        border-radius: 6px;
        overflow: hidden;

        &:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 2px;
        }

        &__ring {
            position: absolute;
            inset: 0;
            background: conic-gradient(
                from var(--angle) at 50% 50%,
                rgb(61,18,155),
                rgb(97,31,139),
                rgb(132,43,122),
                rgb(140,64,77),
                rgb(147,85,31),
                rgb(143,75,54),
                rgb(140,64,77),
                rgb(132,43,122),
                rgb(97,31,139),
                rgb(61,18,155)
            );
            animation: spin-angle 3s linear infinite;
            @keyframes spin-angle {
                to { --angle: 360deg; }
            }
        }
        &__inner {
            position: absolute;
            inset: 4px;
            border-radius: 4px;
            background: linear-gradient(107.8deg, rgb(26,12,40) 0%, rgb(48,31,8) 49.52%, rgb(47,18,40) 100%);
            display: flex;
            align-items: center;
            gap: 8px;
            padding-left: 16px;
        }
        &__label {
            font-family: Inter, sans-serif;
            font-weight: 700;
            font-size: 18px;
            color: white;
            line-height: 1.26;
            text-transform: uppercase;
        }

        :deep(.tag-icon) {
            width: 11px;
            height: 15px;
            transform: rotate(-38deg) skewX(5deg);
            flex-shrink: 0;
        }
    }
}

.menu-header {
    background: var(--navvy, linear-gradient(180deg, rgba(0, 0, 0, 0.90) 0%, rgba(20, 23, 31, 0.00) 100%));
    display: flex;
    justify-content: space-around;

    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
}

.navigation-bar {
    position: sticky;
    top: 0;
    width: 100%;
    height: 88px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0.00) 100%);
    z-index: 100;
    // transition: all 0.2s;

    // using a psudoelement for the solid background
    // so it can be faded in and out via opacity
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, #14161C 0%, #1A1F26 100%);
        opacity: 0;
        z-index: -1;
        transition: opacity 0.25s;
    }

    &.transparent {
        background: transparent;
    }

    &.no-gradient {
        background: var(--surface-500);
    }

    &.bottom-border {
        border-bottom: 2px solid var(--primary-200);
        @include mq-small-and-less {
            border-bottom: none;
        }
    }

    &.scrolled {
        border-bottom: none;

        &::before {
            opacity: 1;
        }

        .nav-link {
            &::before, &::after {
                display:none;
            }
        }

        .triangle-indicator.active {
            display: none;
        }
    }
}

.nav-link {
    color: var(--neutral-100);
    font-weight: 600;
    padding: 12px;
    border-radius: 8px;

    &:hover, &.router-link-exact-active {
        color:var(--primary-200);

    }

    .shop {
        width: 28px;
        height: 28px;
        margin-left: 20px;
    }
}

.item-with-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;

    .triangle-indicator {
        display: none;
        position: absolute;
        bottom: -19px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 14px solid transparent;
        border-right: 14px solid transparent;
        border-bottom: 13.5px solid var(--primary-200);

        @include mq-small-and-less {
            display: none;
        }

        @include mq-large {
            bottom: -16.5px;
        }

        &.active {
            display: block;
        }
    }

    .triangle-indicator::after {
        content: '';
        position: absolute;
        top: 3px;
        left: -11.5px;
        width: 0;
        height: 0;
        border-left: 11.5px solid transparent;
        border-right: 11.5px solid transparent;
        border-bottom: 11.5px solid#242838;
    }
}

.mobile-icon-container {
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-right: -10px;

    &.main-nav {
        gap: 12px;
    }

    &.icons-right {
        gap: 22px;
        margin-right: 12px;
    }

    .sign-in-icon {
        cursor: pointer;
        color: var(--neutral-100);
        width: 17.5px;
        height: 20px;

        &:hover {
            color: var(--primary-200);
        }
    }
}

.nav-wrapper {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.90) 0%, rgba(0, 0, 0, 0.00) 100%);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    margin: auto;
    padding: 16px 80px;

    &.no-gradient {
        background: var(--surface-500);
    }

    @include mq-medium {
        padding: 16px 40px;
    }
}

.sign-in-btn {
    margin-left: 10px;
}

@include mq-small-and-less {
    .logo {
        margin-top: 4px;
    }

    .logo-link {
        text-align: center;
    }

    .main-nav-container {
        width: 33.33%;
        justify-content: right;
    }

    .navigation-bar {
        height: 56px;
    }

    .nav-wrapper {
        padding: 14px 16px;
        align-items: center;

        .profile-icon {
            border-radius: 10px;
            &:hover {
                cursor: pointer;
            }

            &.mobile {
                border: 8px solid var(--surface-800);
                border-radius: 50%;
                height: 100px;
                width: 100px;
                margin-top: 70px;
            }
        }
    }
}
</style>
